'use client';

import { ArticleCard } from '@/sanity/fetchArticles';
import { EventCard } from '@/sanity/fetchEvents';
import {
  URL_PAGE_ALWAYS,
  URL_PAGE_SOON,
  URL_PAGE_TODAY,
  URL_PATH_EVENT,
} from '@/shared/constants';
import 'swiper/css';
import { Swiper, SwiperSlide } from 'swiper/react';
import HIHButton from '../base/HIHButton';
import SingleArticleCard from '../base/SingleArticleCard';
import SingleArticleMobileFullViewCard from '../base/SingleArticleMobileFullviewCard';
import { SingleEventCard } from '../base/SingleEventCard';
import layoutStyles from '@/styles/section-layouts.module.scss';
import styles from './ArticleOrEventRecommendation.module.scss';

interface ArticleOrEventRecommendationProps {
  articles?: ArticleCard[];
  events?: EventCard[];
  variant: 'articles' | 'categories' | 'events';
  title?: string;
  subtitle?: string;
  buttonLabel?: string;
  buttonLink?: string;
}

export const ArticleOrEventRecommendation = ({
  articles = [],
  events = [],
  variant,
  title,
  subtitle,
  buttonLabel,
  buttonLink,
}: ArticleOrEventRecommendationProps) => {
  const articleOrEventList = (
    isLg: boolean,
    events?: EventCard[],
    articles?: ArticleCard[],
  ) => {
    if (events && events.length > 0) {
      return events.map((event: EventCard) =>
        isLg ? (
          <div className={styles.desktopGridItem} key={event._id}>
            <SingleEventCard event={event} variant="secondary" />
          </div>
        ) : (
          <SwiperSlide key={event._id} className={styles.mobileSlide}>
            <SingleEventCard event={event} variant="secondary" />
          </SwiperSlide>
        ),
      );
    } else if (articles && articles.length > 0) {
      return articles.map((article: ArticleCard) =>
        isLg ? (
          <div className={styles.desktopGridItem} key={article._id}>
            <SingleArticleCard article={article} variant="secondary" />
          </div>
        ) : (
          <SwiperSlide key={article._id} className={styles.mobileSlide}>
            <SingleArticleMobileFullViewCard article={article} variant="secondary" />
          </SwiperSlide>
        ),
      );
    }
  };

  const heading = title ?? 'Das könnte auch was für dich sein!';
  const subheading = subtitle ?? 'Noch mehr Empfehlungen warten auf dich.';
  const buttonText =
    buttonLabel ??
    (variant === 'articles'
      ? 'Mehr Guides entdecken'
      : variant === 'events'
        ? 'Mehr Events entdecken'
        : 'Heutige Tipps anschauen');
  const resolvedButtonLink =
    buttonLink ??
    (variant === 'articles'
      ? URL_PAGE_ALWAYS
      : variant === 'events'
        ? URL_PAGE_SOON
        : URL_PAGE_TODAY);

  return (
    <>
      {/* Desktop */}
      <div className={[styles.desktopView, layoutStyles.containerContent].join(' ')}>
        <div className={styles.desktopSidebar}>
          <div className={styles.desktopSidebarText}>
            <h2 className={styles.sectionTitle}>{heading}</h2>
            <p className={styles.sectionSubtitle}>{subheading}</p>
          </div>
          <div className={styles.buttonWrapper}>
            <HIHButton link={resolvedButtonLink} title={buttonText} variant="secondary" />
          </div>
        </div>
        <div className={styles.desktopGrid}>
          {articleOrEventList(true, events, articles)}
        </div>
      </div>

      {/* Mobile */}
      <div className={styles.mobileView}>
        <div
          className={[layoutStyles.containerContent, styles.mobileTextBlock].join(' ')}
        >
          <div className={styles.mobileTitleBlock}>
            <h2 className={styles.sectionTitle}>{heading}</h2>
            <p className={styles.sectionSubtitle}>{subheading}</p>
          </div>
          <div className={styles.buttonWrapper}>
            <HIHButton link={resolvedButtonLink} title={buttonText} variant="secondary" />
          </div>
        </div>
        <Swiper
          spaceBetween={16}
          slidesPerView="auto"
          scrollbar={{
            hide: false,
            draggable: true,
          }}
          className={styles.swiperFull}
          slidesOffsetBefore={16}
          slidesOffsetAfter={16}
        >
          {articleOrEventList(false, events, articles)}
        </Swiper>
      </div>
    </>
  );
};

export default ArticleOrEventRecommendation;
